import './fvb-horizontal-slider.js?v=12';
import './fvb-collapsible-text.js?v=8';
import './fvb-product-tile-action.js?v=16';
import './fvb-product-card-mobile-swiper.js?v=3';
import './fvb-clickable-component.js';

/**
 * Color Carousel Hover – swap the main product card image when hovering a
 * colour-variant thumbnail, and restore it when the cursor leaves.
 */
class FVBProductColorVariant extends HTMLElement {
  constructor() {
    super();
  }
  connectedCallback() {
    this.addEventListener('mouseover', function(e) {
      const item = e.target.closest('.product-card__color-image-carousel__item[data-hover-src]');
      if (!item) return;

      const media = item.closest('product-card')?.querySelector('.product-card__media');
      if (!media) return;

      const img = media.querySelector('img:not(.hidden):not(.hover-img)');
      if (!img) return;

      if (!img.dataset.originalSrc) {
        img.dataset.originalSrc = img.currentSrc || img.src;
        img.dataset.originalSrcset = img.srcset || '';
      }

      img.src = item.dataset.hoverSrc;
      img.srcset = '';
    });

    this.addEventListener('mouseout', function(e) {
      const item = e.target.closest('.product-card__color-image-carousel__item[data-hover-src]');
      if (!item || item.contains(e.relatedTarget)) return;

      const media = item.closest('product-card')?.querySelector('.product-card__media');
      if (!media) return;

      const img = media.querySelector('img[data-original-src]:not(.hover-img)');
      if (!img) return;

      img.src = img.dataset.originalSrc;
      img.srcset = img.dataset.originalSrcset;
      delete img.dataset.originalSrc;
      delete img.dataset.originalSrcset;
    });
  }
}
customElements.define("fvb-product-color-variant", FVBProductColorVariant);

/**
 * Extension of the Drawer to open content in the top of the DOM to prevent z-index issues
 */
class TopDrawer extends MenuDrawer {
  constructor() {
    super();
  }
  connectedCallback() {
    let existingTopDrawer = document.getElementById('top-drawer');
    if (!existingTopDrawer) {
      existingTopDrawer = document.createElement('div');
      existingTopDrawer.setAttribute('id', 'top-drawer');
      existingTopDrawer.classList.add('drawer__container');
      document.body.prepend(existingTopDrawer);
    }

    existingTopDrawer.innerHTML = '';
    existingTopDrawer.appendChild(this.drawer);

    this.topDrawer = existingTopDrawer;
    this.details = null;
  }

  toggleDrawer() {
    const isDrawerOpen = this.topDrawer.classList.contains("menu-opening");
    if (isDrawerOpen) {
      this.topDrawer.classList.remove('menu-opening');
    } else {
      this.topDrawer.classList.add('menu-opening');
    }
    super.toggleDrawer();
  }
}
customElements.define("top-drawer", TopDrawer);


/**
 * Campaign image with product hovers
 */
class FVBCampaignImage extends HTMLElement {
  constructor() {
    super();
  }
  connectedCallback() {
    this.addEventListener('click', function (e) {
      const btn = e.target.closest('.hotspot-btn');

      document.querySelectorAll('.product-popup').forEach((p) => {
        p.classList.remove('active');
      });

      document.querySelectorAll('.hotspot-btn').forEach((b) => {
        b.classList.remove('active');
      });

      if (btn) {
        e.stopPropagation();

        const popup = btn.nextElementSibling;

        this.querySelectorAll('.product-popup').forEach((p) => {
          if (p !== popup) p.classList.remove('active');
        });

        this.querySelectorAll('.hotspot-btn').forEach((b) => {
          if (b !== btn) b.classList.remove('active');
        });

        popup.classList.toggle('active');
        btn.classList.toggle('active');
      }
    });
  }
}
customElements.define("fvb-campaign-image", FVBCampaignImage);


/**
 * Size Filter – handle size selection and display on collection pages
 */
class FVBSizeFilter extends HTMLElement {
  constructor() {
    super();
  }
  connectedCallback() {
    const sizeButtons = this.querySelectorAll('.size-toggle__options button');
    const filterContainer = this.querySelector('.fvb-size-filter');

    if (!filterContainer) return;

    sizeButtons.forEach((button) => {
      button.addEventListener('click', () => {
        sizeButtons.forEach((btn) => btn.classList.remove('size-toggle__option--active'));
        button.classList.add('size-toggle__option--active');
        const selectedSize = button.dataset.size;
        filterContainer.setAttribute('size', selectedSize);
      });
    });

  }
}
customElements.define("fvb-size-filter", FVBSizeFilter);

/**
 * Dynamic accordion
 */
class FVBAccordion extends HTMLElement {
  constructor() {
    super();
  }
  connectedCallback() {
    const enabled = this.dataset.enabled === 'true';
    if (!enabled) {
      this.classList.add('initialized');
      return;
    };

    const contentElements = this.querySelectorAll('.metafield-rich_text_field');
    const accordions = [];

    for (const contentElement of contentElements) {
      let currentAccordionGroup = null;
      let currentAccordion = null;
      for (const elm of contentElement.childNodes) {
        if (elm.nodeType === Node.ELEMENT_NODE && elm.tagName.toLowerCase() === 'h2') {
          if (currentAccordionGroup) {
            if (currentAccordion) {
              currentAccordionGroup.accordions.push({ ...currentAccordion });
              currentAccordion = null;
            }
            accordions.push({ ...currentAccordionGroup });
          }
          currentAccordionGroup = {
            title: elm.innerHTML,
            accordions: []
          };
        } else if (elm.nodeType === Node.ELEMENT_NODE && elm.tagName.toLowerCase() === 'h4') {
          if (currentAccordion) {
            currentAccordionGroup.accordions.push({ ...currentAccordion });
          }
          currentAccordion = {
            title: elm.innerHTML,
            content: ''
          };
        } else if (currentAccordion) {
          currentAccordion.content += elm.outerHTML;
        }
      }
      if (currentAccordion) {
        currentAccordionGroup.accordions.push(currentAccordion);
      }
      if (currentAccordionGroup) {
        accordions.push(currentAccordionGroup);
      }
    }

    this.innerHTML = '';

    for (const group of accordions) {
      const groupEl = document.createElement('div');
      groupEl.className = 'accordions';
      const ts = new Date().getTime();
      groupEl.innerHTML = `
        <h2 class="section__heading h4">${group.title}</h2>
        <accordion-default class="accordion">
          ${group.accordions.map((accordion, index) => `
            <details class="accordion__section" id="Details-${ts}__${index}">
              <summary class="accordion__button h6 js-btn" id="Details-Summary-${ts}__${index}" role="button" aria-expanded="false" aria-controls="Details-Content-${ts}__${index}">
                <div class="accordion__button-text">${accordion.title}</div>
                <div class="accordion__icon">
                  <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M4 9L12 17L20 9" stroke="currentColor" stroke-width="1.0" stroke-linecap="square" stroke-linejoin="square"></path>
                  </svg>
                </div>
              </summary>
              <div class="accordion__body" id="Details-Content-${ts}__${index}" role="region" aria-labelledby="Details-Summary-${ts}__${index}">
                <div class="accordion__body-inner">
                  ${accordion.content}
                </div>
              </div>
            </details>`).join('')}
        </accordion-default>
      `;
      this.appendChild(groupEl);
    }

    this.classList.add('initialized');
  }
}
customElements.define("fvb-accordion", FVBAccordion);

class VideoLightbox extends HTMLElement {
  connectedCallback() {
    this.container = this.querySelector('.ai-video-lightbox-container');
    this.player = this.querySelector('.ai-video-player');
    this.video = this.querySelector('video');

    this.container.addEventListener('click', () => {
      if (this.player.classList.contains('active')) {
        return;
      }

      this.player.classList.add('active');

      this.video.play().then(() => {
        if (this.video.requestFullscreen) {
          this.video.requestFullscreen();
        } else if (this.video.webkitRequestFullscreen) {
          this.video.webkitRequestFullscreen();
        } else if (this.video.msRequestFullscreen) {
          this.video.msRequestFullscreen();
        } else if (this.video.mozRequestFullScreen) {
          this.video.mozRequestFullScreen();
        }
      }).catch(() => {});
    });
  }
}

customElements.define('video-lightbox', VideoLightbox);


class JournalSlider extends HTMLElement {
  connectedCallback() {
    const sectionId = this.dataset.sectionId;
    const root = this.querySelector('.journal-slider');

    if (!root || !sectionId) return;

    const viewport = root.querySelector('[data-journal-viewport]');
    const prev = this.querySelector('.journal-slider__prev-' + sectionId);
    const next = this.querySelector('.journal-slider__next-' + sectionId);
    const controls = this.querySelector('#shopify-section-' + sectionId + ' [data-journal-controls]');
    const scrollbar = this.querySelector('#shopify-section-' + sectionId + ' [data-journal-scrollbar]');
    const scrollbarFill = this.querySelector('#shopify-section-' + sectionId + ' [data-journal-drag]');
    const slides = Array.from(viewport.querySelectorAll('.journal-slider__slide'));

    if (!viewport) return;

    let isDragging = false;
    let dragStartX = 0;
    let dragStartScrollLeft = 0;
    let dragMoved = false;
    const dragThreshold = 6;
    let touchStartX = 0;
    let touchStartY = 0;
    let pendingClientX = null;
    let dragFrame = null;
    let progressFrame = null;
    let scrollEndTimer = null;
    let isTouching = false;

    const getStep = () => {
      const firstSlide = viewport.querySelector('.journal-slider__slide');
      if (!firstSlide) return viewport.clientWidth * 0.8;
      const styles = window.getComputedStyle(viewport.querySelector('.journal-slider__track'));
      const gap = parseFloat(styles.columnGap || styles.gap || 0);
      return firstSlide.getBoundingClientRect().width + gap;
    };

    const snapToNearest = () => {
      const step = getStep();
      if (!step) return;
      const maxScroll = Math.max(viewport.scrollWidth - viewport.clientWidth, 0);

      if (viewport.scrollLeft >= maxScroll - step * 0.5) {
        viewport.scrollTo({ left: maxScroll, behavior: 'smooth' });
        return;
      }
      const target = Math.min(Math.round(viewport.scrollLeft / step) * step, maxScroll);
      viewport.scrollTo({ left: target, behavior: 'smooth' });
    };

    const updateActiveSlide = () => {
      if (!slides.length) return;
      if (window.innerWidth < 1100 && isDragging) return;

      const viewportRect = viewport.getBoundingClientRect();
      const viewportCenter = viewportRect.left + (viewportRect.width / 2);
      let closestSlide = null;
      let closestDistance = Number.POSITIVE_INFINITY;

      slides.forEach((slide) => {
        slide.classList.remove('is-active-lift');
        const rect = slide.getBoundingClientRect();
        const slideCenter = rect.left + (rect.width / 2);
        const distance = Math.abs(slideCenter - viewportCenter);

        if (distance < closestDistance) {
          closestDistance = distance;
          closestSlide = slide;
        }
      });

      if (closestSlide && window.innerWidth < 1100) {
        closestSlide.classList.add('is-active-lift');
      }
    };

    const queueProgressUpdate = () => {
      if (progressFrame) return;
      progressFrame = window.requestAnimationFrame(() => {
        progressFrame = null;
        updateProgress();
      });
    };

    const queueSnapToNearest = () => {
      if (isDragging) return;
      if (scrollEndTimer) {
        window.clearTimeout(scrollEndTimer);
      }
      scrollEndTimer = window.setTimeout(() => {
        snapToNearest();
      }, 180);
    };

    const updateProgress = () => {
      if (!scrollbar || !scrollbarFill) return;
      const slides = Array.from(viewport.querySelectorAll('.journal-slider__slide'));
      const step = getStep();
      const maxScroll = Math.max(viewport.scrollWidth - viewport.clientWidth, 0);
      const visibleSlides = step > 0 ? Math.max(1, Math.floor((viewport.clientWidth + (step * 0.15)) / step)) : 1;
      const totalSlides = Math.max(slides.length, 1);
      const hasPrev = viewport.scrollLeft > 4;
      const hasNext = !(maxScroll <= 0 || viewport.scrollLeft >= maxScroll - 4);
      const dragWidth = totalSlides <= visibleSlides
        ? scrollbar.clientWidth
        : Math.max((visibleSlides / totalSlides) * scrollbar.clientWidth, 64);
      const available = Math.max(scrollbar.clientWidth - dragWidth, 0);
      let progress = maxScroll <= 0 ? 0 : viewport.scrollLeft / maxScroll;

      if (!hasPrev) progress = 0;
      if (!hasNext) progress = 1;

      scrollbar.style.setProperty('--journal-progress-width', `${dragWidth}px`);
      scrollbar.style.setProperty('--journal-progress-x', `${available * progress}px`);
      scrollbarFill.style.width = `${dragWidth}px`;
      scrollbarFill.style.left = `${available * progress}px`;

      if (controls) {
        controls.classList.toggle('has-prev', hasPrev);
        controls.classList.toggle('has-next', hasNext);
      }

      updateActiveSlide();
    };

    prev?.addEventListener('click', () => {
      viewport.scrollBy({ left: -getStep(), behavior: 'smooth' });
    });

    next?.addEventListener('click', () => {
      viewport.scrollBy({ left: getStep(), behavior: 'smooth' });
    });

    const startDrag = (clientX) => {
      isDragging = true;
      dragMoved = false;
      dragStartX = clientX;
      dragStartScrollLeft = viewport.scrollLeft;
      viewport.classList.add('is-dragging');
      slides.forEach((slide) => slide.classList.add('is-drag-lift'));
      slides.forEach((slide) => slide.classList.remove('is-active-lift'));
    };

    const moveDrag = (clientX) => {
      if (!isDragging) return false;
      pendingClientX = clientX;
      if (dragFrame) return dragMoved;
      dragFrame = window.requestAnimationFrame(() => {
        const deltaX = pendingClientX - dragStartX;
        viewport.scrollLeft = dragStartScrollLeft - deltaX;
        dragFrame = null;
      });
      const deltaX = clientX - dragStartX;
      if (Math.abs(deltaX) > dragThreshold) {
        dragMoved = true;
      }
      return dragMoved;
    };

    const endDrag = () => {
      if (!isDragging) return;
      isDragging = false;
      if (dragFrame) {
        window.cancelAnimationFrame(dragFrame);
        dragFrame = null;
      }
      viewport.classList.remove('is-dragging');
      slides.forEach((slide) => slide.classList.remove('is-drag-lift'));
      if (dragMoved) {
        window.requestAnimationFrame(() => {
          snapToNearest();
        });
      } else {
        queueProgressUpdate();
      }
    };

    viewport.addEventListener('dragstart', (event) => {
      event.preventDefault();
    });

    viewport.addEventListener('mousedown', (event) => {
      if (event.button !== 0) return;
      startDrag(event.clientX);
    });

    window.addEventListener('mousemove', (event) => {
      if (!isDragging) return;
      const moved = moveDrag(event.clientX);
      if (moved) {
        event.preventDefault();
      }
    });

    window.addEventListener('mouseup', endDrag);
    window.addEventListener('mouseleave', endDrag);

    viewport.addEventListener('touchstart', (event) => {
      const touch = event.touches[0];
      if (!touch) return;
      isTouching = true;
      touchStartX = touch.clientX;
      touchStartY = touch.clientY;
      dragMoved = false;
    }, { passive: true });

    viewport.addEventListener('touchmove', (event) => {
      const touch = event.touches[0];
      if (!touch) return;
      const deltaX = touch.clientX - touchStartX;
      const deltaY = touch.clientY - touchStartY;

      if (Math.abs(deltaX) > 8 && Math.abs(deltaX) > Math.abs(deltaY)) {
        dragMoved = true;
      }
    }, { passive: true });

    viewport.addEventListener('touchend', () => {
      isTouching = false;
      queueSnapToNearest();
    });

    viewport.addEventListener('touchcancel', () => {
      isTouching = false;
      queueSnapToNearest();
    });

    viewport.querySelectorAll('a').forEach((link) => {
      link.addEventListener('click', (event) => {
        if (dragMoved) {
          event.preventDefault();
          event.stopPropagation();
          dragMoved = false;
        }
      });
    });

    viewport.addEventListener('scroll', () => {
      queueProgressUpdate();
      if (!isTouching && !isDragging) {
        queueSnapToNearest();
      }
    }, { passive: true });
    window.addEventListener('resize', queueProgressUpdate);
    window.addEventListener('load', queueProgressUpdate);
    requestAnimationFrame(queueProgressUpdate);
    setTimeout(queueProgressUpdate, 50);
    setTimeout(queueProgressUpdate, 250);
    queueProgressUpdate();
  }
}

customElements.define('journal-slider', JournalSlider);


class StyledSlider extends HTMLElement {
  connectedCallback() {
    const disabled = this.dataset.enabled === 'false';
    if (disabled) {
      return;
    }

    const root = this;
    const viewport = this.querySelector('.styled-slider__viewport');
    const track = this.querySelector('.styled-slider__track');
    let slides = [];
    const prev = this.querySelector('.styled-slider__control--prev');
    const next = this.querySelector('.styled-slider__control--next');
    const controls = this.querySelector('.styled-slider__controls');
    const scrollbar = this.querySelector('.styled-slider__scrollbar');
    const scrollbarFill = this.querySelector('.styled-slider__scrollbar-drag');
    const filterButtons = Array.from(this.querySelectorAll('[data-styled-filter]'));
    if (!viewport || !track) return;

    document.addEventListener('slider:updated', (event) => {
      if (event.detail.for !== this.dataset.for) return;
      initializeSlider();
    });

    let isDragging = false;
    let dragStartX = 0;
    let dragStartScrollLeft = 0;
    let dragMoved = false;
    let pendingClientX = null;
    let dragFrame = null;
    let progressFrame = null;
    let scrollEndTimer = null;
    let isTouching = false;
    let touchStartX = 0;
    let hasBeenInteracted = false;
    let touchStartY = 0;
    let dragAxis = null;
    const dragThreshold = 6;
    let isStatic = false;

    const refreshSlides = () => {
      slides = Array.from(root.querySelectorAll('.styled-slider__slide')).filter(
        (slide) => !slide.hasAttribute('hidden')
      );
    };

    const getStep = () => {
      const firstSlide = slides[0];
      if (!firstSlide) return viewport.clientWidth * 0.8;
      const styles = window.getComputedStyle(track);
      const gap = parseFloat(styles.columnGap || styles.gap || 0);
      return firstSlide.getBoundingClientRect().width + gap;
    };

    const setDesktopSlideWidth = () => {
      if (!slides.length) return;
      if (window.innerWidth < 1100) return;
      const slidesPerView = parseFloat(root.dataset.slidesPerView) || 4;
      const styles = window.getComputedStyle(track);
      const gap = parseFloat(styles.columnGap || styles.gap || 0);
      const width = Math.max((viewport.clientWidth - (gap * slidesPerView)) / slidesPerView, 0);
      root.style.setProperty('--store-slider-desktop-slide-width', `${width}px`);
    };

    const setTrackPadding = () => {
      const firstSlide = slides[0];
      if (!firstSlide) return;
      if (window.innerWidth >= 1100) {
        const styles = window.getComputedStyle(track);
        const gap = parseFloat(styles.columnGap || styles.gap || 0);
        const slideWidth = firstSlide.getBoundingClientRect().width;
        const totalContentWidth = (slideWidth * slides.length) + (gap * Math.max(slides.length - 1, 0));
        const availableSpace = Math.max(viewport.clientWidth - totalContentWidth, 0);
        const edgeSpace = parseFloat(window.getComputedStyle(root).getPropertyValue('--store-slider-edge-space-desktop')) || 32;
        const sidePadding = availableSpace > 0 ? (availableSpace / 2) : edgeSpace;
        this.style.setProperty('--store-slider-side-padding', `${sidePadding}px`);
        return;
      }
      const sidePadding = Math.max((viewport.clientWidth - firstSlide.getBoundingClientRect().width) / 2, 0);
      this.style.setProperty('--store-slider-side-padding', `${sidePadding}px`);
    };

    const getScrollTargetForIndex = (index) => {
      const slide = slides[index];
      if (!slide) return 0;
      const slidesPerView = parseFloat(root.dataset.slidesPerView) || 1;
      if (slidesPerView > 1) {
        const sidePadding = parseFloat(root.style.getPropertyValue('--store-slider-side-padding')) || 0;
        return slide.offsetLeft - sidePadding;
      }
      return slide.offsetLeft - ((viewport.clientWidth - slide.getBoundingClientRect().width) / 2);
    };

    const getMinScrollTarget = () => {
      return 0;
    };

    const getMaxScrollTarget = () => {
      return Math.max(viewport.scrollWidth - viewport.clientWidth, 0);
    };

    const getClampedScrollTarget = (target) => {
      const min = getMinScrollTarget();
      const max = getMaxScrollTarget();
      return Math.min(Math.max(target, min), max);
    };

    const getSnapTargets = () => {
      const targets = slides.map((_, index) => getClampedScrollTarget(getScrollTargetForIndex(index)));
      return targets.filter((target, index) => index === 0 || Math.abs(target - targets[index - 1]) > 1);
    };

    const getNearestSnapTarget = () => {
      const snapTargets = getSnapTargets();
      if (!snapTargets.length) return 0;

      const current = getClampedScrollTarget(viewport.scrollLeft);
      let nearest = snapTargets[0];
      let smallestDistance = Math.abs(current - nearest);

      snapTargets.forEach((target) => {
        const distance = Math.abs(current - target);
        if (distance < smallestDistance) {
          smallestDistance = distance;
          nearest = target;
        }
      });

      return nearest;
    };

    const scrollToTarget = (target, behavior = 'smooth') => {
      viewport.scrollTo({ left: getClampedScrollTarget(target), behavior });
    };

    const scrollToIndex = (index, behavior = 'smooth') => {
      const clamped = Math.max(0, Math.min(index, slides.length - 1));
      scrollToTarget(getScrollTargetForIndex(clamped), behavior);
    };

    const snapToNearest = (behavior = 'smooth') => {
      scrollToTarget(getNearestSnapTarget(), behavior);
    };

    const updateProgress = () => {
      if (isStatic) {
        if (controls) controls.classList.remove('has-prev', 'has-next');
        if (scrollbarFill) scrollbarFill.hidden = true;
        root.dataset.scrollable = 'false';
        return;
      }
      if (!scrollbar || !scrollbarFill) return;
      const step = getStep();
      const minScroll = getMinScrollTarget();
      const maxScroll = getMaxScrollTarget();
      const scrollRange = Math.max(maxScroll - minScroll, 0);
      const visibleSlides = step > 0 ? Math.max(1, Math.floor((viewport.clientWidth + (step * 0.15)) / step)) : 1;
      const totalSlides = Math.max(slides.length, 1);
      const currentScroll = getClampedScrollTarget(viewport.scrollLeft);
      const snapTargets = getSnapTargets();
      const lastSnapTarget = snapTargets.length > 0 ? snapTargets[snapTargets.length - 1] : maxScroll;
      const hasPrev = currentScroll > minScroll + 4;
      const hasNext = !(scrollRange <= 0 || currentScroll >= lastSnapTarget - 4);
      const dragWidth = totalSlides <= visibleSlides
        ? scrollbar.clientWidth
        : Math.max((visibleSlides / totalSlides) * scrollbar.clientWidth, 64);
      const available = Math.max(scrollbar.clientWidth - dragWidth, 0);
      let progress = scrollRange <= 0 ? 0 : (currentScroll - minScroll) / scrollRange;

      if (!hasPrev) progress = 0;
      if (!hasNext) progress = 1;

      this.style.setProperty('--store-slider-progress-width', `${dragWidth}px`);
      this.style.setProperty('--store-slider-progress-x', `${available * progress}px`);
      scrollbarFill.style.width = `${dragWidth}px`;
      scrollbarFill.style.left = `${available * progress}px`;

      if (controls) {
        controls.classList.toggle('has-prev', hasPrev);
        controls.classList.toggle('has-next', hasNext);
      }
      if (scrollbarFill) {
        scrollbarFill.hidden = slides.length <= 1;
      }
      root.dataset.scrollable = (hasPrev || hasNext) ? 'true' : 'false';
    };

    const queueProgressUpdate = () => {
      if (progressFrame) return;
      progressFrame = window.requestAnimationFrame(() => {
        progressFrame = null;
        updateProgress();
      });
    };

    const queueSnapToNearest = () => {
      if (isDragging) return;
      if (scrollEndTimer) {
        window.clearTimeout(scrollEndTimer);
      }
      scrollEndTimer = window.setTimeout(() => {
        snapToNearest();
      }, 180);
    };

    const startDrag = (clientX) => {
      if (root.dataset.scrollable === 'false') return;
      isDragging = true;
      dragMoved = false;
      dragStartX = clientX;
      dragStartScrollLeft = viewport.scrollLeft;
      viewport.classList.add('is-dragging');
      slides.forEach((slide) => slide.classList.add('is-drag-lift'));
    };

    const moveDrag = (clientX) => {
      if (!isDragging) return false;
      pendingClientX = clientX;
      if (!dragFrame) {
        dragFrame = window.requestAnimationFrame(() => {
          const deltaX = pendingClientX - dragStartX;
          viewport.scrollLeft = getClampedScrollTarget(dragStartScrollLeft - deltaX);
          dragFrame = null;
        });
      }
      const deltaX = clientX - dragStartX;
      if (Math.abs(deltaX) > dragThreshold) {
        dragMoved = true;
      }
      return dragMoved;
    };

    const endDrag = () => {
      if (!isDragging) return;
      isDragging = false;
      if (dragFrame) {
        window.cancelAnimationFrame(dragFrame);
        dragFrame = null;
      }
      viewport.classList.remove('is-dragging');
      slides.forEach((slide) => slide.classList.remove('is-drag-lift'));
      if (dragMoved) {
        window.requestAnimationFrame(() => {
          snapToNearest();
        });
      } else {
        queueProgressUpdate();
      }
    };

    prev?.addEventListener('click', () => {
      if (!slides.length) return;
      const snapTargets = getSnapTargets();
      const current = getNearestSnapTarget();
      const currentIndex = Math.max(snapTargets.findIndex((target) => Math.abs(target - current) <= 1), 0);
      scrollToTarget(snapTargets[Math.max(currentIndex - 1, 0)]);
    });

    next?.addEventListener('click', () => {
      if (!slides.length) return;
      const snapTargets = getSnapTargets();
      const current = getNearestSnapTarget();
      const currentIndex = Math.max(snapTargets.findIndex((target) => Math.abs(target - current) <= 1), 0);
      scrollToTarget(snapTargets[Math.min(currentIndex + 1, snapTargets.length - 1)]);
    });

    viewport.addEventListener('dragstart', (event) => {
      event.preventDefault();
    });

    viewport.addEventListener('mousedown', (event) => {
      if (event.button !== 0) return;
      dragAxis = 'x';
      startDrag(event.clientX);
    });

    window.addEventListener('mousemove', (event) => {
      if (!isDragging) return;
      const moved = moveDrag(event.clientX);
      if (moved) {
        event.preventDefault();
      }
    });

    window.addEventListener('mouseup', endDrag);
    window.addEventListener('mouseleave', endDrag);

    viewport.addEventListener('touchstart', (event) => {
      const touch = event.touches[0];
      if (!touch) return;
      isTouching = true;
      touchStartX = touch.clientX;
      touchStartY = touch.clientY;
      dragAxis = null;
      dragMoved = false;
    }, { passive: true });

    viewport.addEventListener('touchmove', (event) => {
      if (root.dataset.scrollable === 'false') return;
      const touch = event.touches[0];
      if (!touch) return;
      const deltaX = touch.clientX - touchStartX;
      const deltaY = touch.clientY - touchStartY;

      if (!dragAxis) {
        const absX = Math.abs(deltaX);
        const absY = Math.abs(deltaY);
        if (absX < 4 && absY < 4) return;
        if (absX > absY * 1.03 && absX > 5) {
          dragAxis = 'x';
          dragMoved = true;
        } else if (absY > absX) {
          dragAxis = 'y';
        }
      }

      if (dragAxis === 'x') {
        dragMoved = true;
      }
    }, { passive: true });

    viewport.addEventListener('touchend', () => {
      isTouching = false;
      dragAxis = null;
      if (root.dataset.scrollable === 'false') return;
      hasBeenInteracted = true;
      queueSnapToNearest();
    });

    viewport.addEventListener('touchcancel', () => {
      isTouching = false;
      dragAxis = null;
      queueSnapToNearest();
    });

    viewport.querySelectorAll('a').forEach((link) => {
      link.addEventListener('click', (event) => {
        if (dragMoved) {
          event.preventDefault();
          event.stopPropagation();
          dragMoved = false;
        }
      });
    });

    viewport.addEventListener('scroll', () => {
      queueProgressUpdate();
      if (!isTouching && !isDragging) {
        queueSnapToNearest();
      }
    }, { passive: true });

    const initializeSlider = () => {
      refreshSlides();
      if (!slides.length) {
        if (controls) {
          controls.classList.remove('has-prev', 'has-next');
        }
        if (scrollbarFill) {
          scrollbarFill.hidden = true;
        }
        return;
      }
      setDesktopSlideWidth();
      setTrackPadding();

      const minSlides = parseInt(root.dataset.minSlides);
      isStatic = !isNaN(minSlides) && slides.length < minSlides;
      if (isStatic) {
        viewport.scrollTo({ left: 0, behavior: 'auto' });
        updateProgress();
        return;
      }

      if (!hasBeenInteracted) {
        const startIndex = parseInt(root.dataset.startIndex);
        scrollToIndex(isNaN(startIndex) ? Math.floor(slides.length / 2) : startIndex, 'auto');
      }
      queueProgressUpdate();
    };

    window.addEventListener('resize', initializeSlider);
    window.addEventListener('load', initializeSlider);
    requestAnimationFrame(initializeSlider);
    setTimeout(initializeSlider, 60);
    setTimeout(initializeSlider, 220);
    setTimeout(queueProgressUpdate, 400);
  }
}

customElements.define('styled-slider', StyledSlider);

class CustomVariantSelect extends HTMLElement {
  optionElements = [];

  constructor() {
    super();

    this.handleDocumentClick = this.handleDocumentClick.bind(this);
  }

  connectedCallback() {
    const label = this.querySelector('.cvs-label');
    const optionContainer = this.querySelector('.cvs-options-container');
    const guideElement = this.querySelector('.cvs-options-guide');

    this.handleReset();
    this.handleUpdate();

    if (!label || !optionContainer) return;

    this.sortOptions();

    this.optionContainer = optionContainer;
    document.addEventListener('click', this.handleDocumentClick);

    label.addEventListener('click', () => {
      optionContainer.classList.toggle('open');
    });

    if (guideElement) {
      guideElement.addEventListener('click', () => {
        optionContainer.classList.remove('open');
      });
    }

    this.optionElements = this.querySelectorAll('.cvs-option:not(.cvs-option--disabled)');
    this.optionElements.forEach((optionEl) => {
      optionEl.addEventListener('click', () => {
        document.dispatchEvent(new CustomEvent("product:selectVariant", {
          detail: {
            productId: this.dataset.productId,
            value: optionEl.dataset.value,
            optionEl
          },
        }));
        optionContainer.classList.remove('open');
      });
    });
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.handleDocumentClick);
  }

  sortOptions() {
    const optionsWrapper = this.querySelector('.cvs-options');
    if (!optionsWrapper) return;

    const options = Array.from(optionsWrapper.querySelectorAll('.cvs-option'));
    if (options.length < 2) return;

    const decorated = options.map((el, index) => ({
      el,
      index,
      key: CustomVariantSelect.sizeSortKey(el.dataset.label || el.textContent),
    }));

    decorated.sort((a, b) => {
      if (a.key.type !== b.key.type) return a.key.type - b.key.type;
      if (typeof a.key.value === 'number' && typeof b.key.value === 'number') {
        if (a.key.value !== b.key.value) return a.key.value - b.key.value;
      } else {
        const cmp = String(a.key.value).localeCompare(String(b.key.value), undefined, { numeric: true });
        if (cmp !== 0) return cmp;
      }
      return a.index - b.index;
    });

    decorated.forEach(({ el }) => optionsWrapper.appendChild(el));
  }

  static sizeSortKey(rawLabel) {
    const primary = String(rawLabel || '').split('|')[0].trim();
    const norm = primary.replace('½', '.5').replace(',', '.').trim();

    const range = norm.match(/^(\d+(?:\.\d+)?)\s*[-–—]\s*\d+(?:\.\d+)?$/);
    if (range) return { type: 1, value: parseFloat(range[1]) };

    if (/^\d+(?:\.\d+)?$/.test(norm)) return { type: 1, value: parseFloat(norm) };

    const alpha = norm.toUpperCase().replace(/\s+/g, '');
    const scale = {
      XXXXS: -5, '4XS': -5,
      XXXS: -4, '3XS': -4,
      XXS: -3, '2XS': -3,
      XS: -2,
      S: -1,
      M: 0,
      L: 1,
      XL: 2, '1XL': 2,
      XXL: 3, '2XL': 3,
      XXXL: 4, '3XL': 4,
      XXXXL: 5, '4XL': 5,
      XXXXXL: 6, '5XL': 6,
    };
    if (alpha in scale) return { type: 0, value: scale[alpha] };

    const leadingNum = norm.match(/^(\d+(?:\.\d+)?)/);
    if (leadingNum) return { type: 1, value: parseFloat(leadingNum[1]) };

    return { type: 2, value: alpha };
  }

  handleUpdate() {
    document.addEventListener("product:selectVariant", (event) => {
      const { productId, formId, formFieldName, updateUrlParam } = this.dataset;
      const detail = /** @type {CustomEvent} */ (event).detail;

      if (detail.productId !== productId) {
        return;
      }

      this.classList.remove('error');

      if (formId && formFieldName) {
        const form = document.querySelector(`#${formId}`);
        if (form) {
          const formField = form.querySelector(`[name="${formFieldName}"]`);
          if (formField) {
            formField.value = detail.optionEl.dataset.value;
          }
        }
      }

      const select = this.querySelector('select');
      const label = this.querySelector('.cvs-label');

      if (select) {
        select.value = detail.optionEl.dataset.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }

      this.optionElements.forEach((option) => {
        option.classList.remove('cvs-option--selected');
      });
      detail.optionEl.classList.add('cvs-option--selected');

      if (label) {
        label.innerHTML = detail.optionEl.dataset.label;
      }

      if (updateUrlParam) {
        const loc = new URLSearchParams(window.location.search);
        if (detail.optionEl.dataset.value) {
          loc.set(updateUrlParam, detail.optionEl.dataset.value);
        } else {
          loc.delete(updateUrlParam);
        }

        const urlParamsString = loc.toString();
        const urlParamObject = Object.fromEntries(loc);
        history.replaceState(
          urlParamObject,
          "",
          urlParamsString ? `?${urlParamsString}` : location.pathname,
        );
      }

      const isPreOrder = detail.optionEl.dataset.preorder === 'true';
      const btnText = isPreOrder ? window.variantStrings.preOrder : window.variantStrings.addToCart;

      if (formId) {
        const form = document.querySelector(`#${formId}`);
        const productFormEl = form?.closest('product-form');
        if (productFormEl) {
          productFormEl.toggleAttribute('data-has-pre-order', isPreOrder);
          const mainBtn = productFormEl.querySelector('[type="submit"][name="add"]');
          if (mainBtn && !mainBtn.disabled) {
            const span = mainBtn.querySelector('span');
            if (span) {
              for (const node of span.childNodes) {
                if (node.nodeType === Node.TEXT_NODE && node.textContent.trim()) {
                  node.textContent = btnText;
                  break;
                }
              }
            }
          }
        }
      }

      const stickyBtn = document.querySelector('.product__sticky-cart .product-bar__submit');
      if (stickyBtn && !stickyBtn.disabled) {
        const textSpan = stickyBtn.querySelector('span > span');
        if (textSpan) textSpan.textContent = btnText;
      }
    });
  }

  handleReset() {
    document.addEventListener("product:resetVariant", (event) => {
      const { productId, formId, formFieldName } = this.dataset;
      const detail = /** @type {CustomEvent} */ (event).detail;

      if (detail.productId !== productId) {
        return;
      }

      const select = this.querySelector('select');
      if (!select) {
        return;
      }

      this.optionElements.forEach((option) => {
        option.classList.remove('cvs-option--selected');
      });

      select.value = "";
      select.dispatchEvent(new Event('change', { bubbles: true }));

      if (formId && formFieldName) {
        const form = document.querySelector(`#${formId}`);
        if (form) {
          const formField = form.querySelector(`[name="${formFieldName}"]`);
          if (formField) {
            formField.value = "";
          }
        }
      }

      const label = this.querySelector('.cvs-label');
      if (!label) {
        return;
      }

      label.innerHTML = this.dataset.emptyLabel;
    });

    const variantsSize = Number(this.dataset.variants);

    if (!this.dataset.updateUrlParam || variantsSize <= 1) {
      return;
    }
    const loc = new URLSearchParams(window.location.search);
    if (loc.get(this.dataset.updateUrlParam)) {
      return;
    }

    document.addEventListener("DOMContentLoaded", () => {
      document.dispatchEvent(new CustomEvent("product:resetVariant", {
        detail: {
          productId: this.dataset.productId
        }
      }));
    });
  }

  handleDocumentClick(event) {
    if (!this.optionContainer || this.contains(event.target)) return;

    this.optionContainer.classList.remove('open');
  }
}

customElements.define("custom-variant-select", CustomVariantSelect);

/**
 * Service cloud form
 */
class ServiceCloudForm extends HTMLElement {
  constructor() {
    super();

    const queryParam = new URLSearchParams(window.location.search);
    if (queryParam.get('confirmation') === '1') {
      this.classList.add('has-confirmation');
    } else {
      this.classList.add('is-active');
    }
  }

  connectedCallback() {
    const form = this.querySelector('form');

    if (!form) return;

    const inputWrappers = form.querySelectorAll('.fvb-input-wrapper');
    inputWrappers.forEach((wrapper) => {
      const input = wrapper.querySelector('input, textarea, select');
      if (!input) return;

      input.addEventListener('focus', () => {
        wrapper.classList.add('is-focused');
      });

      input.addEventListener('blur', () => {
        wrapper.classList.remove('is-focused');
        if (input.value) {
          wrapper.classList.add('has-value');
          if (input.checkValidity()) {
            wrapper.classList.remove('error');
            wrapper.classList.add('success');
          } else {
            wrapper.classList.add('error');
            wrapper.classList.remove('success');
          }
        } else {
          wrapper.classList.remove('has-value');
          if (input.required) {
            wrapper.classList.add('error');
            wrapper.classList.remove('success');
          }
        }
      });
    });

    // const labels = this.querySelectorAll('label');
    // labels.forEach((label) => {
    //   const inputId = label.getAttribute('for');
    //   if (!inputId) return;
    //   const input = this.querySelector(`#${inputId}`);
    //   if (!input) return;

    //   label.style.setProperty('display', `none`);
    //   input.setAttribute('placeholder', label.textContent.trim());
    // });
  }
}
customElements.define("service-cloud-form", ServiceCloudForm);
